import * as m from "mithril";
import * as ApiDTOs from "./apiclient/ApiDtos";
import { ApiAccessType, ApiError, ApiMode, IApiClient } from "./apiclient/IApiClient";
import Either from "./types/Either";
import SearchCriteria from "./models/SearchCriteria";
import { ApiAccess, ApiClient } from "./apiclient/ApiClient";



export function buildApiService(accessToken: string, apiBaseUrl: string): ApiService {
    const apiAccessType: ApiAccessType = { mode: ApiMode.Direct, bearerToken: accessToken };
    let apiAccess: ApiAccess = new ApiAccess(apiAccessType);
    return new ApiService(new ApiClient(apiBaseUrl, apiAccess));
}

export class ApiService {
    apiClient: IApiClient;
    constructor(apiClient: IApiClient) {
        this.apiClient = apiClient;
    }
    
    getMasterData(appId: string): Promise<Either<ApiError, ApiDTOs.MasterDataDTO>> {
        return this.apiClient.getMasterData(appId);
    }

    getInitialSourceLocations(appId: string): Promise<Either<ApiError, ApiDTOs.SourceLocationDTO[]>> {
        return this.apiClient.getInitialSourceLocations(appId);
    }
    
    searchTrips(criteria: SearchCriteria): Promise<Either<ApiError, ApiDTOs.TripResultAggregateDTO>> {
        const tripSearchCriteria: ApiDTOs.TripSearchCriteria = {
            sourceId: criteria.sourceLocation.id,
            destinationId: criteria.destinationLocation.id,
            departureDate: criteria.isoDateString,
            numberOfSeats: criteria.numberOfSeats,
            isForForeigner: criteria.travellerType == 'Foreigner'
        }
        return this.apiClient.searchTrips(tripSearchCriteria);
    }

    getArticle(name: string): Promise<Either<ApiError, ApiDTOs.ArticleDTO>> {
        return this.apiClient.getArticle(name);
    }

    getTrip(tripId: string): Promise<Either<ApiError, ApiDTOs.TripDTO>> {
        return this.apiClient.getTripDetails(tripId);
    }

    getOperatorPhotos(operatorId: string): Promise<Either<ApiError, string[]>> {
        return this.apiClient.getOperatorPhotos(operatorId);
    }

    createTripCustimazion(
        tripId: string,
        numberOfSeats: number,
        seatSelections: ApiDTOs.SeatSelectionDTO[],
        pickUpOrder: ApiDTOs.PickUpOrderRequestDTO | null,
        dropOffOrder: ApiDTOs.DropOffOrderRequestDTO | null

    ): Promise<Either<ApiError, ApiDTOs.TripCustomizationDTO>> {
        const tripCustomization: ApiDTOs.TripCustomizationCreateRequest = {
            tripId: tripId,
            seatOrder: {
                numberOfSeats: numberOfSeats,
                seatSelections: seatSelections
            },
            pickUpOrder: pickUpOrder != null ? pickUpOrder : undefined,
            dropOffOrder: dropOffOrder != null ? dropOffOrder : undefined
        }

        return this.apiClient.createTripCustomization(tripCustomization);
    }

    updateTripCustimazion(
        tripCustomizationId: string,
        pickUpOrder: ApiDTOs.PickUpOrderRequestDTO | null,
        dropOffOrder: ApiDTOs.DropOffOrderRequestDTO | null
    ): Promise<Either<ApiError, ApiDTOs.TripCustomizationDTO>> {
        const tripCustomizationUpdateRequest: ApiDTOs.TripCustomizationUpdateRequestDTO = {
            pickUpOrder: pickUpOrder != null ? pickUpOrder : undefined,
            dropOffOrder: dropOffOrder != null ? dropOffOrder : undefined
        }
        return this.apiClient.updateTripCustomization(
            tripCustomizationId, tripCustomizationUpdateRequest
        );
    }

    getTripCustimazion(
        tripCustomizationId: string,
    ): Promise<Either<ApiError, ApiDTOs.TripCustomizationDTO>> {
        return this.apiClient.getTripCustomization(tripCustomizationId)
    }


    createTentativeBooking(
        tripCustomizationId: string,
        travellerInfo: ApiDTOs.TravellerDTO

    ): Promise<Either<ApiError, ApiDTOs.TentativeBookingDTO>> {
        const tentativeBooking: ApiDTOs.TentativeBookingCreateRequestDTO = {
            tripCustomizationId: tripCustomizationId,
            traveller: travellerInfo
        }
        return this.apiClient.createTentativeBooking(tentativeBooking);
    }




    updateTentativeBooking(
        tentativeBookingId: string,
        travellerInfo: ApiDTOs.TravellerDTO

    ): Promise<Either<ApiError, ApiDTOs.TentativeBookingDTO>> {

        const tentativeBooking: ApiDTOs.TentativeBookingUpdateRequestDTO = {
            traveller: travellerInfo
        }
        return this.apiClient.updateTentativeBooking(tentativeBookingId, tentativeBooking);
    }


    getTentativeBooking(tentativeBookingId: string): Promise<Either<ApiError, ApiDTOs.TentativeBookingDTO>> {

        return this.apiClient.getTentativeBooking(tentativeBookingId);
    }


    getPaymentOptions(billId: string): Promise<Either<ApiError, ApiDTOs.PaymentOptionDTO[]>> {
        return this.apiClient.getPaymentOptions(billId);
    }

    verifyPromoCode(
        billId: string,
        paymentMethodCode: string,
        promoCode: string
    ): Promise<Either<ApiError, ApiDTOs.PromoCodeVerifiedResponseDTO>> {

        const promoVerificationReq: ApiDTOs.PromoCodeVerificationRequestDTO = {
            billId: billId,
            paymentMethodCode: paymentMethodCode,
            promoCode: promoCode
        }

        return this.apiClient.verifyPromoCode(promoVerificationReq);
    }


    createPaymentIntent(
        request: ApiDTOs.PaymentIntentCreateRequestDTO
    ): Promise<Either<ApiError, ApiDTOs.PaymentIntentDTO>> {
        return this.apiClient.createPaymentIntent(request)
    }

    createMiniAppPaymentInitiation(billId: string, paymentMethodCode: string, promoCodeVerificationRef?: string): Promise<Either<ApiError, ApiDTOs.MiniappPaymentIntentDTO>> {
        let paymentRequest: ApiDTOs.PaymentIntentCreateRequestDTO = {
            billId: billId,
            mode: 'MINI_APP',
            paymentMethod: new ApiDTOs.PaymentMethodDTO(paymentMethodCode),
            promoCodeVerificationRef: promoCodeVerificationRef
        }
        return this.apiClient.createMiniAppPaymentInitiation(paymentRequest);
    }

    searchBookingRequest(tentativeBookingId: string): Promise<Either<ApiError, ApiDTOs.BookingRequestDTO>> {
        throw new Error("Method not implemented.");
    }
    searchBookingRequests(appUserId: string | undefined, pageNo: number, pageSize: number): Promise<Either<ApiError, ApiDTOs.BookingRequestResultsAggregateDTO>> {
        throw new Error("Method not implemented.");
    }
    getBookingRequestDetails(bookingRequestId: string): Promise<Either<ApiError, ApiDTOs.BookingRequestDTO>> {
        throw new Error("Method not implemented.");
    }
    checkBooking(tentativeBookingId: string): Promise<Either<ApiError, ApiDTOs.BookingDTO>> {
        return this.apiClient.checkBooking(tentativeBookingId);
    }
    searchBookings(pageNo: number, pageSize: number): Promise<Either<ApiError, ApiDTOs.BookingResultSet>> {
        return this.apiClient.searchBookings(pageNo, pageSize);
    }
    getBookingDetails(bookingId: string): Promise<Either<ApiError, ApiDTOs.BookingDTO>> {
        return this.apiClient.getBookingDetails(bookingId)
    }

}